import React, { useRef, useState, useEffect, memo } from 'react';
import { css } from '@emotion/react';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { useTranslation } from '@frontend/i18n';
import { AiOfficeUser, AiOfficeUserRouted } from '@frontend/phone-ui';
import { Photos } from '@frontend/photos';
import { theme } from '@frontend/theme';
import { Text, styles as dsStyles, useTooltip } from '@frontend/design-system';

type OfficeUserNameProps = {
  disableClick?: boolean;
  displayName: string;
  maxWidth?: number;
  showAvatar?: boolean;
  profileName?: string;
  userId?: string;
};

type OfficeUserNameOldProps = {
  firstName?: string;
  isDataUnavailable?: boolean;
  isInactive?: boolean;
  isWeaveUser?: boolean;
  lastName?: string;
  maxWidth?: number;
  textAlign?: 'left' | 'center' | 'right';
  userId?: string;
  showAvatar?: boolean;
};

export const OfficeUserName: React.FC<OfficeUserNameProps> = ({
  disableClick = true,
  profileName,
  displayName,
  maxWidth = 200,
  showAvatar = true,
  userId,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });
  const [isOverflown, setIsOverflown] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflown(el.scrollWidth > el.clientWidth);
    }
  }, [displayName, maxWidth]);

  return (
    <div css={styles.wrapper}>
      {showAvatar && (
        <Photos.ContactProfilePhoto
          personId={userId ?? ''}
          name={profileName}
          disableClick={disableClick}
          size='small'
          isUser
        />
      )}
      <span {...(isOverflown ? triggerProps : {})} css={styles.nameWrapper(maxWidth)}>
        <Text ref={textRef} size='medium' css={[dsStyles.truncate]}>
          {displayName}
        </Text>
      </span>
      {isOverflown && (
        <Tooltip {...tooltipProps}>
          <Text size='medium' color='white'>
            {displayName}
          </Text>
        </Tooltip>
      )}
    </div>
  );
};

export const OfficeUserNameWithAi = ({
  disableClick = true,
  profileName,
  displayName,
  maxWidth = 200,
  showAvatar = true,
  userId,
  outcome,
}: OfficeUserNameProps & {
  outcome?: AIOutcome;
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });

  if (
    outcome === AIOutcome.AI_OUTCOME_END_CALL ||
    outcome === AIOutcome.AI_OUTCOME_FATAL ||
    outcome === AIOutcome.AI_OUTCOME_ROUTE_MISSED
  ) {
    return <AiOfficeUser />;
  }

  if (outcome === AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED) {
    return <AiOfficeUserRouted name={profileName} userId={userId} />;
  }

  return (
    <div
      style={{
        display: 'grid',
        gap: theme.spacing(0.5),
        alignItems: 'center',
        gridAutoFlow: 'column',
      }}
    >
      {showAvatar && (
        <Photos.ContactProfilePhoto
          personId={userId ?? ''}
          // Only show the first initial since this is a small avatar
          name={profileName ? profileName.split(' ')[0] : undefined}
          title={profileName}
          disableClick={disableClick}
          size='small'
          isUser
          css={{ width: 20, height: 20, minWidth: 20, minHeight: 20 }}
        />
      )}
      <span {...triggerProps} css={styles.nameWrapper(maxWidth)}>
        <Text ref={textRef} size='medium' css={[dsStyles.truncate, { padding: 1 }]}>
          {displayName}
        </Text>
      </span>

      <Tooltip {...tooltipProps}>
        <Text size='medium' color='white'>
          {displayName}
        </Text>
      </Tooltip>
    </div>
  );
};

export const OfficeUserNameOld = memo(
  ({
    firstName,
    isDataUnavailable,
    isInactive,
    lastName,
    maxWidth = 200,
    showAvatar = false,
    textAlign,
    userId,
    isWeaveUser = false,
    ...rest
  }: OfficeUserNameOldProps) => {
    const { t } = useTranslation('call-intel-core');
    const textRef = useRef<HTMLParagraphElement>(null);
    const { Tooltip: Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });
    const [isValueTruncated, setIsValueTruncated] = useState<boolean | null>(false);
    const name = `${firstName || ''} ${lastName || ''}`.trim();

    useEffect(() => {
      const isValueTruncated = textRef.current && textRef.current.scrollWidth > textRef.current.clientWidth;
      setIsValueTruncated(isValueTruncated);
    }, [textRef.current]);

    return (
      <span
        {...(isValueTruncated ? triggerProps : {})}
        style={{
          color: isInactive ? theme.colors.neutral50 : 'none',
          display: 'flex',
          gap: isValueTruncated ? 0 : theme.spacing(0.5),
          justifyContent: textAlign,
          width: '100%',
          alignItems: 'center',
        }}
        {...rest}
      >
        {showAvatar && (
          <Photos.ContactProfilePhoto
            personId={userId || ''}
            name={name || t('Unknown')}
            disableClick
            size='small'
            isUser={isWeaveUser}
          ></Photos.ContactProfilePhoto>
        )}
        <Text
          as='span'
          css={dsStyles.truncate}
          ref={textRef}
          style={{
            color: isDataUnavailable ? theme.colors.neutral50 : 'inherit',
            display: 'block',
            flex: 'unset',
            maxWidth,
          }}
        >
          {isDataUnavailable ? t('Unavailable Data') : name || t('Unknown')}
        </Text>
        {isInactive && !isDataUnavailable && name && (
          <Text as='span' color='inherit' style={{ color: 'inherit' }}>
            ({t('Inactive')})
          </Text>
        )}
        {!!isValueTruncated && (
          <Tooltip {...tooltipProps}>
            <Text size='medium' color='white'>
              {isDataUnavailable
                ? t('Unavailable Data')
                : name
                ? isInactive
                  ? t('{{name}} (Inactive)', { name })
                  : name
                : t('Unknown')}
            </Text>
          </Tooltip>
        )}
      </span>
    );
  }
);

OfficeUserNameOld.displayName = 'OfficeUserNameOld';

const styles = {
  wrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  nameWrapper: (maxWidth: number) =>
    css({
      maxWidth,
    }),
};
